import React from 'react';
import { motion } from 'framer-motion';
import { LandPlot, Layers, FileCheck2 } from 'lucide-react';

const features = [
  {
    title: "AnyRoR Integration",
    description: "Direct pipeline to government land records. Fetch current ownership, plot type, and survey numbers instantly.",
    icon: LandPlot,
    gradient: "from-orange-500 to-red-500",
    bg: "bg-orange-50"
  },
  {
    title: "AUDA TP Overlays",
    description: "High-precision GIS layers showing Final Plots, proposed roads, reservations, and zoning boundaries.",
    icon: Layers,
    gradient: "from-blue-500 to-cyan-500",
    bg: "bg-blue-50"
  },
  {
    title: "GDCR Compliance",
    description: "Automated regulatory checks. Get instant calculations for FSI, margins, and height restrictions.",
    icon: FileCheck2,
    gradient: "from-purple-500 to-indigo-500",
    bg: "bg-purple-50"
  }
];

export const Solution: React.FC = () => {
  return (
    <section id="solution" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
            Unified Intelligence
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We bring the three essential pillars of town planning into a single, coherent interface.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Hover Gradient Border Effect */}
              <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
              
              <div className={`${feature.bg} w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-7 h-7 text-gray-800" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">{feature.description}</p>

              <div className="flex items-center text-sm font-semibold text-gray-900 cursor-pointer group-hover:translate-x-2 transition-transform">
                  Learn more <span className="ml-1">→</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
